import POLICY_DATA_LIST from '@kidzonet/consts/src/policyDataList'
import { defineStore } from '@kidzonet/pinia'
import {
    activateProtectionOff as activateProtectionOffAPI,
    activateProtectionOn as activateProtectionOnAPI,
    fetchPolicyList,
    setPolicyList,
} from '@kidzonet/ts-api-dashboard-policy'

interface policyItemInterface {
    name: string
    single: boolean
    id?: number
    isActive?: boolean
}

type policyListType = policyItemInterface[]

export const useDasboardPolicyStore = defineStore('dashboard-policy', {
    state: () => {
        const policyList: policyListType = []
        return {
            policyList,
            loading: false,
        }
    },
    getters: {
        activePolicies (): policyListType {
            let result = this.policyList.filter(({ isActive }) => isActive)
            if (result.length === 0) {
                // no active policies return allow all
                result = this.policyList.filter(({ name }) => name === 'allow_all')
            }
            return result
        },
        activePolicy (): policyItemInterface {
            return this.activePolicies.filter(({ single }) => single)[0]
        },
        singlePolicies: ({ policyList }) => policyList.filter(({ single }) => single),
        policySwitchers: ({ policyList }) => policyList.filter(({ single }) => !single),
        policy: ({ policyList }) => (policyId: number) => policyList.find(({ id }) => policyId === id),
        policyByName: ({ policyList }) => (policyName: string) => policyList.find(({ name }) => name === policyName),
        policyBlockSocialMedia (): policyItemInterface | undefined {
            return this.policyByName('social_media')
        },
        onPolicy (): policyItemInterface {
            // @ts-expect-error result can be undefined but it means that something didn't loaded
            return this.policyByName('holiday_time')
        },
        offPolicy (): policyItemInterface {
            // @ts-expect-error result can be undefined but it means that something didn't loaded
            return this.policyByName('allow_all')
        },
        activeSinglePolicy (): policyItemInterface {
            if (this.onPolicy.isActive) {
                return this.onPolicy
            }
            return this.offPolicy
        },
        inactiveSinglePolicy (): policyItemInterface {
            if (this.onPolicy.isActive) {
                return this.offPolicy
            }
            return this.onPolicy
        },
    },
    actions: {
        async fetchUserPolicies () {
            this.loading = true
            const policyList = await fetchPolicyList()
            if (policyList) {
                const result: policyListType = POLICY_DATA_LIST.map((item) => {
                    const itemFromAPI = policyList.find(({ name }) => name === item.name)
                    return { ...item, ...itemFromAPI }
                })
                this.policyList = result
            }
            this.loading = false
        },
        async activatePolicy (id?: number) {
            if (this.loading) {
                return
            }
            if (!id) {
                return
            }
            this.loading = true
            let activatedPolicyList: number[] = []
            const checkedPolicy = this.policy(id)
            let requestPolicyListResult = false
            if (id !== undefined && checkedPolicy) {
                if (checkedPolicy.single) {
                    activatedPolicyList = [id, ...this.activePolicies.filter(
                        policy => !policy.single && policy.id !== undefined,
                    ).map(item => item.id || 0)]
                } else {
                    activatedPolicyList = this.activePolicies.filter(
                        policy => policy.id !== undefined,
                    ).map(item => item.id || 0)
                    if (checkedPolicy.isActive) {
                        activatedPolicyList = activatedPolicyList.filter(item => item !== id)
                    } else {
                        activatedPolicyList.push(id)
                    }
                }
                if (activatedPolicyList.length > 0) {
                    // prevent single selected policy deactivate
                    requestPolicyListResult = await setPolicyList(
                        activatedPolicyList,
                    )
                }
            }

            if (requestPolicyListResult) {
                this.policyList = this.policyList.map((item) => {
                    item.isActive = activatedPolicyList.includes(item.id || 0)
                    return item
                })
            }

            this.loading = false
        },
        async activateProtectionOn () {
            if (this.loading) {
                return
            }
            this.loading = true
            try {
                const result = await activateProtectionOnAPI()
                if (result) {
                    this.policyList = this.policyList.map((item) => {
                        if (item.name === 'holiday_time') {
                            item.isActive = true
                        } else if (item.name === 'allow_all') {
                            item.isActive = false
                        }
                        return item
                    })
                }
            } finally {
                this.loading = false
            }
        },
        async activateProtectionOff (ui: 'simple' | 'full') {
            if (this.loading) {
                return
            }
            this.loading = true
            try {
                const result = await activateProtectionOffAPI(ui === 'simple')
                if (result) {
                    this.policyList = this.policyList.map((item) => {
                        if (item.name === 'holiday_time') {
                            item.isActive = false
                        } else if (item.name === 'allow_all') {
                            item.isActive = true
                        }
                        return item
                    })
                }
            } finally {
                this.loading = false
            }
        },
        async activateOneClickPolicy (id?: number) {
            if (this.loading) {
                return
            }
            if (!id) {
                return
            }
            this.loading = true
            const idList = [id]
            if (this.policyBlockSocialMedia && this.policyBlockSocialMedia.id && this.policyBlockSocialMedia.isActive) {
                idList.push(this.policyBlockSocialMedia.id)
            }
            const requestPolicyListResult = await setPolicyList(idList)

            if (requestPolicyListResult) {
                this.policyList = this.policyList.map((item) => {
                    if (item.id) {
                        item.isActive = idList.includes(item.id)
                    }
                    return item
                })
            }

            this.loading = false
        },
    },
})
